<template>
  <div class="form-item-container mb-6">
    <v-tooltip v-if="!userCanEditFormItems" activator="parent" location="center">
      {{ $t('i18n.editNotAvailable') }}
    </v-tooltip>
    <div class="fixed-content-container">
      <div class="header pt-2 pb-2">
        <div class="header-title">
          <v-icon class="item-icon cursor-move" icon="mdi-drag-vertical" size="large"></v-icon>
          <div class="font-weight-black">{{ $t('i18n.ratingInputTitle') }}</div>
        </div>
        <div class="header-tool-icons">
          <v-select v-model="itemData.rating_type_id" :items="ratingTypesList" item-title="name" item-value="id"
            variant="outlined" density="compact" :placeholder="$t('i18n.selectRatingType')" min-width="15em"
            :rules="selectRules" :no-data-text="$t('i18n.ratingTypesNotAvailable')"
            :disabled="!userCanEditFormItems"></v-select>
          <v-btn class="cursor-pointer" variant="text" icon @click="duplicateItem" :disabled="!userCanEditFormItems">
            <v-icon icon="$gorateDuplicate"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.duplicate') }}
            </v-tooltip>
          </v-btn>
          <v-btn class="cursor-pointer" variant="text" icon @click="removeItem" :disabled="!userCanEditFormItems">
            <v-icon size="small" icon="$gorateDeleteOutline"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.remove') }}
            </v-tooltip>
          </v-btn>
          <v-btn class="cursor-pointer" variant="text" icon @click="expandGrid" :disabled="!userCanEditFormItems">
            <v-icon :icon="expandGridButtonIconName" size="large"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.collapse') }}
            </v-tooltip>
          </v-btn>
        </div>
      </div>
    </div>
    <div ref="collapsableContentContainer" class="collapsable-content-container">
      <div style="overflow: hidden">
        <div class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language1') }}</div>
          <v-text-field v-model="itemData.question_lang1" density="compact" :placeholder="$t('i18n.questionLang1')"
            variant="outlined" dense hide-details="auto" clearable :rules="questionRules" counter
            :disabled="!userCanEditFormItems"></v-text-field>
        </div>
        <div v-if="bilingual" class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language2') }}</div>
          <v-text-field v-model="itemData.question_lang2" density="compact" :placeholder="$t('i18n.questionLang2')"
            variant="outlined" dense hide-details="auto" clearable :rules="questionRules" counter></v-text-field>
        </div>
        <v-card class="pt-2" variant="flat">
          <div v-if="itemData.rating_type_id === 1" class="rating-type-container">
            <v-rating :model-value="3" :size="30" color="yellow-darken-3" readonly disabled></v-rating>
          </div>
          <div v-if="itemData.rating_type_id === 2" class="rating-type-container">
            <EmoticonFace1 class="mr-3" />
            <EmoticonFace2 class="mr-3" />
            <EmoticonFace3 class="mr-3" />
            <EmoticonFace4 class="mr-3" />
          </div>
        </v-card>
      </div>
    </div>
  </div>
  <v-divider :thickness="2" class="border-opacity-100 mb-3" color="grey"></v-divider>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue'
import { FormDeviceAssociation, FormItem, RatingType } from '@/utils/DataTypes'
import EmoticonFace4 from '../icons/EmoticonFace4.vue'
import EmoticonFace3 from '../icons/EmoticonFace3.vue'
import EmoticonFace2 from '../icons/EmoticonFace2.vue'
import EmoticonFace1 from '../icons/EmoticonFace1.vue'
import { useAppStore } from '@/stores/appStore'

export default defineComponent({
  name: 'RatingInput',
  setup () {
    const appStore = useAppStore()

    const collapsableContentContainer = ref<HTMLElement | null>(null)
    const isExpanded = ref(true)

    const expandGrid = () => {
      if (collapsableContentContainer.value) {
        isExpanded.value = !isExpanded.value
        collapsableContentContainer.value.style.gridTemplateRows = isExpanded.value ? '1fr' : '0fr'
      }
    }

    return {
      appStore,
      collapsableContentContainer,
      isExpanded,
      expandGrid
    }
  },
  emits: ['duplicate-item', 'remove-item', 'form-item-update'],
  props: {
    formList: {
      type: Array as PropType<FormDeviceAssociation[]>,
      required: true,
      default: () => []
    },
    indexInTheSketchForm: {
      type: Number,
      required: true
    },
    bilingual: {
      type: Boolean,
      required: false,
      default: false
    },
    formItems: {
      type: Array as PropType<FormItem[]>,
      required: false,
      default: null
    },
    userCanEditFormItems: {
      type: Boolean,
      required: true,
      default: true
    }
  },
  components: {
    EmoticonFace4,
    EmoticonFace3,
    EmoticonFace2,
    EmoticonFace1
  },
  async mounted () {
    this.ratingTypesList = this.appStore.getAppState.ratingTypesList
  },
  data () {
    return {
      ratingTypeError: '',

      selectedRatingType: { id: -1, name: '', description: '', num_steps: -1 } as unknown as RatingType,
      ratingTypesList: [] as RatingType[],

      itemData: {
        name: 'RATING',
        item_type_id: 4,
        question_lang1: '',
        question_lang2: '',
        required: true,
        bilingual: this.bilingual,
        rating_type_id: null
      } as FormItem
    }
  },
  watch: {
    formItems: {
      handler () {
        if (this.formItems) {
          if (this.formItems[this.indexInTheSketchForm] !== undefined) {
            this.itemData = this.formItems[this.indexInTheSketchForm]
          }
        }
      },
      immediate: true
    },
    itemData: {
      handler (newItemData) {
        this.$emit('form-item-update', { formItemIndex: this.indexInTheSketchForm, data: newItemData })
      },
      deep: true
    },
    bilingual: {
      handler (newBilingual) {
        this.itemData.bilingual = newBilingual
        if (!newBilingual) {
          this.itemData.question_lang2 = ''
        }
      },
      immediate: true
    }
  },
  methods: {
    validateRatingType () {
      if (!this.itemData.rating_type_id) {
        this.ratingTypeError = 'Rating type must be selected.'
      } else {
        return true
      }
    },
    duplicateItem () {
      this.$emit('duplicate-item', this.indexInTheSketchForm)
    },
    removeItem () {
      this.$emit('remove-item', this.indexInTheSketchForm)
    }
  },
  computed: {
    questionRules (): ((value: string) => boolean | string)[] {
      return [
        (value: any) => {
          if (value) return true
          return this.$t('i18n.questionRequired')
        }, (value: any) => {
          if (value && value.length <= 180) return true
          return this.$t('i18n.questionMaxLength', { max: 180 })
        }
      ]
    },
    selectRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.ratingTypeRequired')
        }
      ]
    },
    expandGridButtonIconName (): string {
      if (this.isExpanded) return 'mdi-chevron-up'
      else return 'mdi-chevron-down'
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../styles/colors.scss";

.form-item-container {

  .fixed-content-container {
    .header {
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr 1fr;
      align-items: start;

      .header-title {
        grid-column: 1;
        display: grid;
        grid-template-columns: auto auto;
        justify-self: start;
        align-items: center;
      }

      .header-tool-icons {
        grid-column: 2;
        justify-self: end;
        display: grid;
        grid-template-columns: 1fr repeat(3, auto);
        align-items: start;
      }

    }
  }

  .collapsable-content-container {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 300ms;

    .body {
      display: grid;
      grid-template-columns: 6.5em 1fr;
      align-items: center;
    }

    .rating-type-container {
      display: flex;
      justify-content: center;
    }
  }

}
</style>
